import React, { createContext, useContext, useState, useRef, ReactNode } from "react";

type ConfirmOptions = {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "info";
};

type ConfirmContextType = {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useConfirm must be used within a ConfirmProvider");
  return context;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolveRef = useRef<(value: boolean) => void>(() => {});

  const confirm = (opts?: ConfirmOptions) => {
    setOptions({
      title: "Are you sure?",
      description: "This action cannot be undone.",
      confirmText: "Confirm",
      cancelText: "Cancel",
      variant: "danger",
      ...opts,
    });
    setIsOpen(true);
    return new Promise<boolean>((resolve) => { resolveRef.current = resolve; });
  };

  const handleClose = (value: boolean) => {
    setIsOpen(false);
    resolveRef.current(value);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => handleClose(false)} />
          <div className="relative w-full max-w-sm overflow-hidden rounded-xl border border-white/10 bg-[#09090b] shadow-2xl animate-in zoom-in-95 duration-200">
            <div className={`h-1 w-full ${options.variant === 'danger' ? 'bg-red-500' : 'bg-cyan-500'}`} />
            <div className="p-6">
              <h3 className="text-lg font-bold text-white mb-2">{options.title}</h3>
              <p className="text-xs text-white/60 leading-relaxed mb-6">{options.description}</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => handleClose(false)} className="px-4 py-2 rounded-lg border border-white/10 bg-white/5 text-xs font-bold text-white hover:bg-white/10 transition-colors">
                  {options.cancelText}
                </button>
                <button onClick={() => handleClose(true)} className={`px-4 py-2 rounded-lg border text-xs font-bold transition-all shadow-lg ${options.variant === 'danger' ? 'border-red-500/30 bg-red-500/20 text-red-200 hover:bg-red-500/30' : 'border-cyan-500/30 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30'}`}>
                  {options.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}
